import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from "../../../hooks/useAuthContext";
import {
    Box,
    Typography,
    Grid,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    IconButton,
    InputAdornment,
    Switch,
    FormControlLabel,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Snackbar
} from '@mui/material';
import TemplateContainer from "../edit/TemplateContainer";
import TemplateContainerViewOnly from "./TemplateContainerViewOnly";
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import TemplateExportButton from "./TemplateExportButton";
import { 
    getTemplate, 
    updateTemplate, 
    createTemplate,
    addSharedUser,
    removeSharedUser,
    findUserByDisplayName
} from '../../../lib/firestore';

const TemplateView = () => {
    const { id } = useParams();
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [statistics, setStatistics] = useState({});
    const [editableName, setEditableName] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [gearBySlot, setGearBySlot] = useState({});
    const [inventory, setInventory] = useState({
        "Torso": [], "Helm": [], "Arms": [], "Hands": [], "Legs": [], "Feet": [],
        "Necklace": [], "Cloak": [], "Jewel": [], "Belt": [], "L Ring": [], "R Ring": [],
        "L Wrist": [], "R Wrist": [], "Mythirian": [], "Main Hand": [], "Off Hand": [],
        "Two-Hand": [], "Ranged": []
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [sharedUserDisplayName, setSharedUserDisplayName] = useState('');
    const [sharedUsers, setSharedUsers] = useState([]);

    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                const fetchedTemplate = await getTemplate(id);

                if (fetchedTemplate) {
                    setTemplate(fetchedTemplate);
                    setIsOwner(user?.uid === fetchedTemplate.owner);
                    
                    if (!fetchedTemplate.isPrivate || 
                        fetchedTemplate.owner === user?.uid || 
                        fetchedTemplate.sharedUsers?.some(u => u.uid === user?.uid)) {
                        setHasAccess(true);
                    }

                    if (fetchedTemplate.items) {
                        setGearBySlot(fetchedTemplate.items);
                    }
                    
                    if (fetchedTemplate.sharedUsers) {
                        setSharedUsers(fetchedTemplate.sharedUsers);
                    }

                    // Start with the existing inventory from the template, if available
                    const updatedInventory = fetchedTemplate.inventory ? { ...fetchedTemplate.inventory } : { ...inventory };

                    // Add new items from gearBySlot to the inventory
                    Object.keys(fetchedTemplate.items || {}).forEach(slot => {
                        const itemInSlot = fetchedTemplate.items[slot];
                        if (itemInSlot && !updatedInventory[slot].some(item => item.id === itemInSlot.id)) {
                            updatedInventory[slot] = [...(updatedInventory[slot] || []), itemInSlot];
                        }
                    });
                    setInventory(updatedInventory);
                } else {
                    console.log('Template not found.');
                }
            } catch (error) {
                console.error('Error fetching template:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchTemplate();
    }, [id, user?.uid]);

    const handleSaveTemplate = async () => {
        try {
            if (!user) {
                console.error('User not authenticated');
                return;
            }

            const isPrivate = template.isPrivate !== undefined ? template.isPrivate : true;

            await updateTemplate(id, {
                ...template,
                items: gearBySlot,
                inventory: inventory,
                isPrivate: isPrivate,
                lastUpdated: new Date(),
            });

            setSaveStatus('Template saved successfully');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error saving template:', error);
            setSaveStatus('Error saving template');
            setSnackbarOpen(true);
        }
    };

    const handleLike = async () => {
        if (!user) return;
        if (template && user) {
            const currentLikes = template.likes || [];
            const updatedLikes = currentLikes.includes(user.uid) ?
                currentLikes.filter(uid => uid !== user.uid) :
                [...currentLikes, user.uid];

            await updateTemplate(id, { likes: updatedLikes });
            setTemplate({ ...template, likes: updatedLikes });
        }
    };

    const handleOpenCopyDialog = () => {
        setCopyDialogOpen(true);
    };

    const handleCloseCopyDialog = () => {
        setCopyDialogOpen(false);
        setNewTemplateName('');
    };

    const handleCopyTemplate = async () => {
        if (!user || !template) return;
        handleCloseCopyDialog();

        const currentDate = new Date();
        try {
            const newTemplate = {
                ...template,
                name: newTemplateName,
                owner: user.uid,
                ownerName: user.displayName,
                isPrivate: true,
                createdOn: currentDate,
                lastUpdated: currentDate,
            };
            delete newTemplate.id;

            const newTemplateId = await createTemplate(newTemplate);
            navigate(`/templates/${newTemplateId}`);
        } catch (error) {
            console.error('Error copying template:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTemplate({ ...template, [name]: value });
    };

    const handleEditNameClick = () => {
        setEditableName(true);
    };

    const handleSaveNameClick = () => {
        setEditableName(false);
        handleSaveTemplate();
    };

    const handleCancelNameClick = () => {
        setEditableName(false);
        setTemplate({ ...template, name: template.name });
    };

    const handleNameKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSaveNameClick();
        }
    };

    const handleAddSharedUser = async () => {
        if (sharedUserDisplayName) {
            try {
                const userData = await findUserByDisplayName(sharedUserDisplayName);
                
                if (userData) {
                    const newUser = { uid: userData.uid, displayName: userData.displayName };
                    if (!sharedUsers.some(user => user.uid === newUser.uid)) {
                        await addSharedUser(id, newUser);
                        setSharedUsers([...sharedUsers, newUser]);
                    }
                } else {
                    console.error('User not found');
                }
            } catch (error) {
                console.error('Error adding shared user:', error);
            }
            setSharedUserDisplayName('');
        }
    };

    const handleRemoveSharedUser = async (uid) => {
        try {
            await removeSharedUser(id, uid);
            setSharedUsers(sharedUsers.filter(user => user.uid !== uid));
        } catch (error) {
            console.error('Error removing shared user:', error);
        }
    };

    const handleStatisticsCalculated = (calculatedStatistics) => {
        setStatistics(calculatedStatistics);
    };

    if (isLoading) {
        return <Typography>Loading...</Typography>;
    }

    if (!template) {
        return <Typography>Template not found.</Typography>;
    }

    if (!hasAccess) {
        return <Typography>You do not have access to view this template.</Typography>;
    }

    const isLikedByUser = template.likes ? template.likes.includes(user?.uid) : false;

    return (
        <React.Fragment>
            <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={4} md={3} lg={4} xl={4}>
                    <Typography variant="body1">
                        {isOwner && !editableName ? (
                            <>
                                {template.name}
                                <IconButton
                                    aria-label="edit"
                                    size="small"
                                    onClick={handleEditNameClick}
                                >
                                    <EditIcon fontSize="inherit" />
                                </IconButton>
                            </>
                        ) : isOwner && editableName ? (
                            <>
                                <TextField
                                    fullWidth
                                    type="text"
                                    name="name"
                                    value={template.name}
                                    onChange={handleInputChange}
                                    onKeyDown={handleNameKeyPress}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="save"
                                                    size="small"
                                                    onClick={handleSaveNameClick}
                                                >
                                                    <CheckIcon fontSize="inherit" />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="cancel"
                                                    size="small"
                                                    onClick={handleCancelNameClick}
                                                >
                                                    <CancelIcon fontSize="inherit" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        ) : (
                            template.name
                        )}
                    </Typography>
                </Grid>

                <Grid container item xs={12} sm={8} md={9} lg={4} xl={4} justifyContent="space-between" alignItems="center">
                    <Grid item xs={4} sm={4} md={4} lg={3} xl={2}>
                        <Typography variant="body1">
                            <strong>Owner:</strong> {template.ownerName}
                        </Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={3} xl={2}>
                        <Typography variant="body1">
                            <strong>Realm:</strong> {template.realm}
                        </Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={3} xl={2}>
                        <Typography variant="body1">
                            <strong>Class:</strong> {template.class}
                        </Typography>
                    </Grid>
                    <Grid item xs={4} sm={4} md={4} lg={3} xl={3}>
                        <Typography variant="body1">
                            <strong>Level:</strong> {template.level}
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container item xs={12} sm={12} md={12} lg={3} xl={3} justifyContent="flex-end" alignItems="center">
                    <Grid item xs={9} sm={9} md={9} lg={6} xl={6} sx={{ order: { sm: 1, md: 1, lg: 1, xl: 1 } }}>
                        {isOwner ? (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!template.isPrivate}
                                        onChange={(e) => setTemplate({ ...template, isPrivate: !e.target.checked })}
                                    />
                                }
                                label={template.isPrivate ? "Private" : "Public"}
                            />
                        ) : (
                            <Typography variant="body1">
                                <strong>Status:</strong> {template.isPrivate ? "Private" : "Public"}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={3} sm={3} md={3} lg={6} xl={6} sx={{ order: { sm: 2, md: 2, lg: 2, xl: 2 } }}>
                        <Box display="flex" justifyContent="flex-end">
                            {!template.isPrivate && (
                                <Box ml={2}>
                                    <TemplateExportButton template={template} statistics={statistics} id={id} />
                                </Box>
                            )}
                            {isOwner && (
                                <Box ml={2}>
                                    <Button variant="contained" color="primary" onClick={handleSaveTemplate}>
                                        Save
                                    </Button>
                                </Box>
                            )}
                            {isOwner && (
                                <Box ml={2}>
                                    <Button onClick={() => setDrawerOpen(true)} variant="outlined">
                                        Share
                                    </Button>
                                </Box>
                            )}
                            {user && (
                                <Box ml={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleOpenCopyDialog}
                                        startIcon={<FileCopyIcon />}
                                    >
                                        Copy
                                    </Button>
                                </Box>
                            )}
                            {user && user.uid !== template.owner && (
                                <Box ml={2}>
                                    <Button
                                        variant="contained"
                                        color={isLikedByUser ? "secondary" : "primary"}
                                        onClick={handleLike}
                                        startIcon={isLikedByUser ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                                    >
                                        {isLikedByUser ? "Unlike" : "Like"}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Grid>

            <hr />
            <Grid container spacing={2} mt={2} justifyContent="space-between" alignItems="center">
                {isOwner ? (
                    <TemplateContainer 
                        template={template} 
                        gearBySlot={gearBySlot} 
                        setGearBySlot={setGearBySlot} 
                        inventory={inventory} 
                        setInventory={setInventory} 
                        onStatisticsCalculated={handleStatisticsCalculated} 
                        statistics={statistics} 
                    />
                ) : (
                    <TemplateContainerViewOnly
                        template={template}
                        gearBySlot={template.items}
                        onStatisticsCalculated={handleStatisticsCalculated}
                    />
                )}
            </Grid>

            {/* Copy Template Dialog */}
            <Dialog open={copyDialogOpen} onClose={handleCloseCopyDialog}>
                <DialogTitle>Copy Template</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Enter a name for the new template:
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Template Name"
                        type="text"
                        fullWidth
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCopyDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleCopyTemplate} color="primary">
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Share Drawer */}
            {isOwner && (
                <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                    <Box
                        sx={{ width: 250 }}
                        role="presentation"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <List>
                            {sharedUsers.map((user) => (
                                <ListItem key={user.uid} secondaryAction={
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleRemoveSharedUser(user.uid)}>
                                        <DeleteIcon />
                                    </IconButton>
                                }>
                                    <ListItemText primary={`${user.displayName || user.uid}`} />
                                </ListItem>
                            ))}
                        </List>
                        <Box p={2}>
                            <Box mb={2}>
                                <TextField
                                    fullWidth
                                    label="Friend's Display Name"
                                    value={sharedUserDisplayName}
                                    onChange={(e) => setSharedUserDisplayName(e.target.value)}
                                />
                            </Box>
                            <Button variant="contained" onClick={handleAddSharedUser}>
                                Add User
                            </Button>
                        </Box>
                    </Box>
                </Drawer>
            )}

            {/* Snackbar for save status */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={() => setSnackbarOpen(false)}
                message={saveStatus}
            />
        </React.Fragment>
    );
};

export default TemplateView;
